import mdAxios from 'core/common/config/axios-config';
import {SECURABLE_TYPE, MODULE, PERMISSION, ISECURABLE, PRIMITIVE, TSECURABLE} from "core/metamodel/metaConstant";
import last = require("lodash/last");
import {AxiosRequestConfig} from "axios";
import {LovItem} from "core/metamodel/mdEntity";
import {action, observable} from "mobx";
import {LoginObject} from "core/security/loginForm";
import {
  UserContextDto, UserSession, Tenant, User,
  SecurityRole, PARAMKEY, UserToken, RestrictionData, SecurableDefinitionModuleMap, UserProfileDto
} from "core/security/securityDomain";
import {IDomainObject} from "core/form/formUtils";



class SecurityService  {

  @observable loginObject: LoginObject;
  @observable currentUser: UserSession;
  @observable currentTenant: Tenant;
  @observable availableTenants: LovItem[];
  @observable restrictionData: RestrictionData;

  @observable userObject: User;
  @observable passwordObject: User;
  @observable userProfileObject: UserProfileDto;
  @observable securityRoleObject: SecurityRole;
  @observable securables: SecurableDefinitionModuleMap;
  @observable tenantParams: { [TKey in PARAMKEY]: PRIMITIVE };
  @observable tenantObject: Tenant;


  setUserContextDto(userContextDto: UserContextDto) {
    this.currentUser = new UserSession(userContextDto.userSession);
    this.currentTenant = new Tenant(userContextDto.tenant);
    this.availableTenants = userContextDto.availableTenants;
    this.tenantParams = userContextDto.tenantParams;
    this.restrictionData = new RestrictionData(userContextDto.restrictionData);
  }


  @action processUserContext = (response): UserContextDto => {
    console.log("processUserContext ", response);
    let userContextDto: UserContextDto = response.data;
    let tenantIdStr = userContextDto.tenant.tenantId.toString();
    window.sessionStorage.setItem('tenantId', tenantIdStr);
    window.TENANT_ID = tenantIdStr;
    // clear localStorare
    window.localStorage.clear();
    securityService.setUserContextDto(userContextDto);

    return userContextDto;
  };

  login(username: string, password: string): Promise<UserContextDto> {
    return mdAxios.get('/core/security/current-user-context', {
      headers: {
        authorization: "Basic " + btoa(username + ":" + password)
      }
    }).then(this.processUserContext);
  }

  impersonate(username: string): Promise<UserContextDto> {
    return mdAxios.post("/core/security/impersonate/" + username)
      .then(this.processUserContext);
  }



  logout() {
    return mdAxios.post('/core/security/logout');
  }

  readCurrentUserContext(config?: AxiosRequestConfig): Promise<UserContextDto> {
    console.log('Reading current user');
    return mdAxios.get('/core/security/current-user-context', config).then((response) => {
      return response.data;
    }, (error) => {
      if (error.response.status === 401) {
        console.log('Not authenticated - 401 from readCurrentUser()');
        return null;
      } else {
        throw error;
      }
    });
  }

  getLastTenantId(username: string): Promise<Number> {
    return mdAxios.get('/public/last-tenant-id', {
      params: {username}
    }).then(response => response.data);
  }


  getTenantParamFromServer(tenantId: number, paramKey: PARAMKEY): Promise<string|number> {
    return mdAxios.get('/public/tenant-param/' + tenantId + '/' + paramKey).then(response => response.data);
  }


  readUser(username: string): Promise<User> {
    return mdAxios.get('/core/security/user/' + username)
      .then(response => new User(response.data));
  }

  readUserProfile(username: string): Promise<UserProfileDto> {
    return mdAxios.get('/core/security/user-profile/' + username)
      .then(response => new UserProfileDto(response.data));
  }

  saveUserProfile(username: string, userProfileDto: UserProfileDto): Promise<IDomainObject> {
    return mdAxios.post('/core/security/user-profile/' + username, userProfileDto)
      .then((response) => response.data);
  }


  userExist(username: string): Promise<boolean> {
    return mdAxios.get('/core/security/user-exist/' + username)
      .then(response => response.data);
  }

  userCreate(user): Promise<IDomainObject> {
    return mdAxios.put('/core/security/user', user, {showPrettyError: true})
      .then((response) => response.data);
  }
  userUpdate(user): Promise<IDomainObject> {
    return mdAxios.post('/core/security/user', user, {showPrettyError: true})
      .then((response) => response.data);
  }

  deleteUser(username: string): Promise<any> {
    return mdAxios.delete('/core/security/user/' + username, {showPrettyError: true});
  }

  changeLastTenant(tenantId: number): Promise<any> {
    return mdAxios.post("/core/security/change-tenant/" + tenantId);
  }

  resetPassword(user: User, mode: string): Promise<any> {
    return mdAxios.post('/core/security/user-reset-password/' + mode, user, {showPrettyError: true});
  }


  readSecurityRole(roleId: string): Promise<SecurityRole> {
    return mdAxios.get('/core/security/security-role/' + roleId)
      .then(response => new SecurityRole(response.data));
  }

  saveSecurityRole(securityRole: SecurityRole): Promise<IDomainObject> {
    return mdAxios.post('/core/security/security-role', securityRole)
      .then((response) => response.data);
  }


  deleteSecurityRole(roleId: string): Promise<any> {
    return mdAxios.delete('/core/security/security-role/' + roleId, {showPrettyError: true});
  }


  getSecurables(): Promise<SecurableDefinitionModuleMap> {
    return (this.securables) ? Promise.resolve(this.securables)
      : mdAxios.get('/core/security/securables').then(response => response.data);
  }

  permissionList(securable: TSECURABLE): PERMISSION[] {
    return  this.currentUser && this.currentUser.permissionList(securable);
  }

  hasRight(securable: TSECURABLE, permission: PERMISSION, detailId?: string): boolean {
    return  this.currentUser && this.currentUser.hasRight(securable, permission, detailId);
  }

  /**
   *
   * @param router
   * @param module
   * @param securableType
   * @param securable - string nebo {name: string, module: string} - potom ma toto module prednost pred module prop a module z page
   * @param permission
   * @param detailId
   * @returns {*}
   */
  hasRouterRight(router, module: MODULE, securableType: SECURABLE_TYPE, securable: ISECURABLE|string, permission: PERMISSION, detailId?: string): boolean {
    //console.debug('hasRouterRight ', module, securableType, securable, permission);
    if (permission) {
      let lastRoute:any = null;
      let resolvedSecurable = securable;
      if (!module) {
        lastRoute = last(router.getCurrentRoutes());
        module = lastRoute.handler.module;
      }
      if (!securable) {
        if (!lastRoute) lastRoute = last(router.getCurrentRoutes());
        resolvedSecurable = lastRoute.name;
      } else {
        if (typeof securable === 'object' && securable.name) {
          if (securable.module) module = securable.module;
          if (securable.type) securableType = securable.type;
          resolvedSecurable = securable.name;
        }
      }
      return this.hasRight( resolvedSecurable as TSECURABLE, permission, detailId);
    } else {
      return true;
    }
  }

  getTenantParam(param: PARAMKEY): PRIMITIVE {
    return (this.tenantParams && this.tenantParams[param]);
  }

  generateToken(username: string, userToken: UserToken): Promise<UserToken> {
    return mdAxios.post('/core/security/user-token-generate/' + username , userToken)
      .then((response) => new UserToken(response.data));
  }

  deleteToken(tokenId: string): Promise<any> {
    return mdAxios.delete('/core/security/user-token/' + tokenId);
  }


}

const securityService = new SecurityService();
export default securityService;




// WEBPACK FOOTER //
// ./src/core/security/securityService.ts